import type { LSChildAccount } from '../../shared/types/child-account';
import { useChildAccountStore } from './useChildAccountStore';

interface UseChildAccountsOptions {
	stateKey?: string;
	onLogout?: () => Promise<void>;
}

interface AccountDescriptor {
	name: string;
	hashID: string;
}

/**
 * A log-safe descriptor of a child account: a display name plus its hashID. The
 * `name` key is intentionally left unmasked (bare `name` is not in the scrub's
 * RECOMMENDED_RULES) so support can read the account at a glance in the collector.
 */
function describeAccount(account: LSChildAccount | null | undefined): AccountDescriptor | null {
	if (!account) return null;
	return {
		name: account.first_name || account.name || account.company || account.hashID,
		hashID: account.hashID,
	};
}

/** The switchable account options (excludes the synthetic logout entry). */
function describeAccountOptions(accounts: LSChildAccount[]): AccountDescriptor[] {
	return accounts
		.filter((account) => account.hashID !== 'logout')
		.map(describeAccount)
		.filter((entry): entry is AccountDescriptor => entry !== null);
}

/**
 * A scrub-disabled child of the currently-active logger for the child-account
 * breadcrumbs. Called inside a `frogger.span(...)` it parents under that span
 * (`frogger.child` resolves to the active span logger); at root it is a child of the
 * root logger. These logs carry only the operator's own account descriptors (names +
 * hashIDs) and booleans — never secrets — so scrub is disabled here to keep the names
 * readable in the collector (the global scrub otherwise keepEnds-masks
 * `selectedName`/`accountName`).
 *
 * Only pass `(msg, ctx)` through this logger: a raw IFroggerLogger does NOT run the
 * ambient facade's positional-`Error` normalization, so failure logs that pass a caught
 * `Error` positionally stay on the ambient `frogger` (their `name`-keyed descriptors are
 * unscrubbed by the global rules anyway).
 */
function accountLog() {
	return frogger.child({ scrub: false });
}

export function useChildAccounts(options: UseChildAccountsOptions = {}) {
	const { stateKey = 'lcp-child-accounts', onLogout } = options;

	const nuxtApp = useNuxtApp();
	const { $api, $apiAuth } = nuxtApp;

	const { isStaticMode, getFullUrl } = useStaticMode();

	const store = useChildAccountStore({ key: stateKey });

	// Shared with useAuth (same key + shape); factory only runs if this
	// composable is somehow created before useAuth registers the state.
	const authState = useState<{
		isLoggedIn: boolean;
		isLoggingIn: boolean;
		userID: string | null;
	}>('lcp-auth', () => ({
		isLoggedIn: false,
		isLoggingIn: false,
		userID: null,
	}));

	const state = useState<{
		selectedAccount: LSChildAccount | null;
		lastSelectedAccount: LSChildAccount | null;
		isChangingUser: boolean;
		accountsError: string | null;
		userID: string | null;
	}>(`${stateKey}-selection`, () => ({
		selectedAccount: null,
		lastSelectedAccount: null,
		isChangingUser: false,
		accountsError: null,
		userID: null,
	}));

	// Add logout option to the accounts list
	const childAccountsWithLogout = computed<LSChildAccount[]>(() => {
		const accounts = store.data.value || [];
		const hasLogout = accounts.some((a) => a.userID === 'logout');
		if (hasLogout) return accounts;

		return [
			...accounts,
			{
				userID: 'logout',
				company: 'Logout',
				hashID: 'logout',
				parent: false,
				name: 'Logout',
				first_name: 'Logout',
				last_name: 'Logout',
			},
		];
	});

	const childAccountsOptions = computed<string[]>(() => {
		return childAccountsWithLogout.value
			.map((account) => account.first_name || account.name || account.company)
			.filter((name): name is string => typeof name === 'string' && name.length > 0);
	});

	const getSelectedAccountOption = computed({
		get: () => {
			const account = state.value.selectedAccount;
			return account?.first_name || account?.name || account?.company;
		},
		set: async (accountName: string | null | undefined) => {
			if (typeof accountName !== 'string') {
				// Clears (null/undefined) are expected from select components;
				// any other type means a caller leaked a non-selection value onto
				// the model channel (e.g. the open/close boolean regression).
				if (accountName !== null && accountName !== undefined) {
					frogger.warn('ignored non-string account selection', {
						valueType: typeof accountName,
					});
				}
				return;
			}
			if (!accountName) return;

			if (accountName === 'Logout') {
				const previousAccount = state.value.selectedAccount;
				await nextTick();

				const confirm = window.confirm('Are you sure you want to log out?');
				if (!confirm) {
					state.value.selectedAccount = null;
					await nextTick();
					state.value.selectedAccount = previousAccount;
					return;
				}

				if (onLogout) {
					await onLogout();
				}
				return;
			}

			const associatedAccount = childAccountsWithLogout.value.find((account) => {
				return (
					account.first_name === accountName ||
					account.name === accountName ||
					account.company === accountName
				);
			});

			if (associatedAccount) {
				const previousAccount = state.value.selectedAccount;

				// Re-selecting the active account is a no-op: the selector just
				// closes (UiSelect hides itself on option click) and no
				// ChangeUser/GetUser round-trip fires.
				if (associatedAccount.hashID === previousAccount?.hashID) {
					return;
				}

				state.value.isChangingUser = true;
				state.value.selectedAccount = associatedAccount;
				state.value.lastSelectedAccount = associatedAccount;

				await changeUser(associatedAccount.hashID, previousAccount);
				state.value.isChangingUser = false;
			} else {
				accountLog().warn('no account matched selection', { selectedName: accountName });
			}
		},
	});

	async function fetchChildAccounts(): Promise<LSChildAccount[]> {
		state.value.accountsError = null;

		return await frogger.span('fetch-child-accounts', async () => {
			try {
				await store.refresh();
				const accounts = store.data.value || [];

				// Auto-select if only one account
				if (accounts.length === 1) {
					state.value.selectedAccount = accounts[0] ?? null;
				} else if (!state.value.selectedAccount && accounts.length > 0) {
					// Otherwise select first parent account or first account
					const firstAccount =
						accounts.find((account) => account.parent) ?? accounts[0] ?? null;
					state.value.selectedAccount = firstAccount;
				}

				accountLog().info('child accounts loaded', {
					count: accounts.length,
					selected: describeAccount(state.value.selectedAccount),
					options: describeAccountOptions(accounts),
				});

				return accounts;
			} catch (err) {
				frogger.error('failed to load child accounts', err);
				state.value.accountsError = 'Failed to load child accounts';
				return [];
			}
		});
	}

	/**
	 * The backend rejects an auth-less ChangeUser with 400 rather than 401, so
	 * the API layer's 401 refresh-and-retry never runs for a dead session and
	 * the app would otherwise stay in a half-authenticated shell. Probe the
	 * session with a refresh: alive means the 400 was a genuine bad request
	 * (keep the switch error message); dead means log out cleanly. A failed
	 * probe while the browser knows it is offline is a transient network drop,
	 * not session death, so it must not log the user out.
	 */
	/** Returns true when the probe found the session alive (token rotated, safe to retry). */
	async function recoverIfSessionDead(error: unknown): Promise<boolean> {
		const err = error as { status?: number; statusCode?: number };
		if ((err?.status ?? err?.statusCode) !== 400) return false;

		const newToken = await $apiAuth.attemptTokenRefresh();
		if (newToken) {
			$apiAuth.authenticate(newToken);
			return true;
		}
		if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
		$apiAuth.forceLogout('account-switch-session-dead');
		return false;
	}

	async function changeUser(
		hashID: string,
		fromAccount?: LSChildAccount | null,
	): Promise<void> {
		const targetAccount =
			childAccountsWithLogout.value.find((account) => account.hashID === hashID) ?? null;
		const from = describeAccount(fromAccount ?? state.value.selectedAccount);
		const to = describeAccount(targetAccount);
		const options = describeAccountOptions(store.data.value ?? []);

		// Account descriptors are the operator's own managed accounts and carry no
		// secrets, so these breadcrumbs log through a scrub-disabled child (see
		// accountLog()) to keep names/hashIDs readable in the collector.
		accountLog().debug('child account switch requested', {
			from,
			to,
			options,
			optionCount: options.length,
			hashID,
		});

		if (!hashID || hashID === 'logout') {
			return;
		}

		// Switching to the already-active account is a no-op — never fire the
		// ChangeUser/GetUser round-trip. Compare against fromAccount when the
		// caller supplied one (the selector passes the pre-switch selection,
		// possibly a legitimate null) else fall back to the live selection.
		// Not `??`: the selector mutates selectedAccount to the target BEFORE
		// calling in, so coalescing an explicit null would always self-match.
		const activeAccount =
			fromAccount !== undefined ? fromAccount : state.value.selectedAccount;
		if (activeAccount?.hashID === hashID) {
			accountLog().debug('child account switch skipped: already active', {
				from,
				to,
				hashID,
			});
			return;
		}

		// A session can die while the page sits open (device left idle); a
		// ChangeUser without live auth is a guaranteed backend rejection, so
		// block it here and surface the expired session instead of firing a
		// doomed request.
		if (!authState.value?.isLoggedIn) {
			accountLog().warn('child account switch blocked: not authenticated', {
				from,
				to,
				hashID,
			});
			state.value.accountsError = 'Your session has expired. Please log in again.';
			return;
		}

		let switchedUserID: string | null = null;
		let switched = false;

		await frogger.span('account-switch', async () => {
			// One retry: a 400 whose recovery probe finds the session alive means
			// the access token was merely stale (e.g. rotated by a concurrent
			// refresh), not that the switch itself was invalid. Without retrying,
			// the token gets silently repaired but the switch the user asked for
			// never happens - they're left on the old account with just an error
			// message and have to reselect the target themselves.
			let retriedAfterRecovery = false;
			for (let attempt = 0; attempt < 2; attempt++) {
				try {
					const response = await $api<{ accessToken: string }>(
						getFullUrl('/api/Auth/ChangeUser'),
						{
							method: 'POST',
							body: { hashID },
							credentials: 'include',
						},
					);

					if (!response?.accessToken) {
						throw new Error('Access token not found in response');
					}
					$apiAuth.authenticate(response.accessToken);
					state.value.accountsError = null;
					switched = true;
					break;
				} catch (error) {
					if (attempt === 0 && (await recoverIfSessionDead(error))) {
						retriedAfterRecovery = true;
						accountLog().debug('retrying child account switch after session recovery', {
							from,
							to,
							hashID,
						});
						continue;
					}

					// Positional Error -> ambient frogger (normalizes it); `from`/`to` use the
					// `name` key which the global scrub leaves readable.
					frogger.error('child account switch failed', error, { from, to, hashID });
					state.value.accountsError = 'Failed to switch account';
					return;
				}
			}

			try {
				switchedUserID = await $api<string>(getFullUrl('/api/Auth/GetUser'), {
					credentials: 'include',
				});
				state.value.userID = switchedUserID;
			} catch (error) {
				frogger.warn('failed to load user id after switch', error, { from, to, hashID });
				state.value.accountsError = 'Failed to load user ID';
			}

			// scrub-disabled child, created inside the span so it parents under it.
			accountLog().info('child account switched', {
				from,
				to,
				options,
				optionCount: options.length,
				hasAccessToken: true,
				hashID,
				retriedAfterRecovery,
			});
		});

		// Re-stamp the ROOT logger identity AFTER the span. A span mutates only its own
		// scoped context (it's discarded when the span unwinds), so an addContext INSIDE
		// the span would be lost; done here it targets the root logger, so every
		// subsequent log's correlation follows the now-active account. app.vue only
		// stamps `user` on the login transition — without this a switched session keeps
		// the previous user's id (the user-vs-customerID mismatch seen in the collector).
		if (switchedUserID) {
			frogger.addContext({ user: switchedUserID });
		}

		// A swap re-authenticates in place, so every cache fetched under the
		// previous account is now stale. Fire the app-wide invalidation hook (a
		// client plugin clears all account-scoped Nuxt data and refetches what is
		// on screen). Guarded on the switch actually happening: a failed
		// ChangeUser leaves the user on the old account with its data intact. Runs
		// even when the post-switch GetUser failed, because the account did change.
		// A refetch failure here must never fail the switch itself.
		if (switched) {
			try {
				await nuxtApp.callHook('app:account-switched', {
					toHashID: hashID,
					userID: switchedUserID,
				});
			} catch (error) {
				frogger.warn('post-switch data invalidation failed', error, { hashID });
			}
		}
	}

	function reset(): void {
		state.value.selectedAccount = null;
		state.value.lastSelectedAccount = null;
		state.value.accountsError = null;
		state.value.userID = null;
	}

	return {
		childAccounts: childAccountsWithLogout,
		childAccountsOptions,

		selectedAccount: computed(() => state.value.selectedAccount),
		lastSelectedAccount: computed(() => state.value.lastSelectedAccount),
		isLoadingAccounts: store.fetching,
		accountsError: computed(() => state.value.accountsError),
		isChangingUser: computed(() => state.value.isChangingUser),
		currentUserID: computed(() => state.value.userID),
		getSelectedAccountOption,

		fetchChildAccounts,
		changeUser,
		reset,
	};
}
